<script setup lang="ts">
import SearchField from '@/components/filter/SearchField.vue';
import { I18nKey } from '@/utils/typings';
import type { TextDto } from '@/api/generator';
import type { TextOverviewFilterObject, TextScopeFilterObject } from '../types';
import Autocomplete from '@/components/forms/Autocomplete/Autocomplete.vue';
import { SelectItemTypeObject } from '@/components/forms/types';
import { useTranslation } from 'i18next-vue';
import { getTranslationPrefix } from '@/utils/translationPrefix';
import PtwResetFilterButton from '@/components/buttons/PtwResetFilterButton.vue';
import { uniqBy } from 'lodash';
import { computed } from 'vue';
import { truthy } from '@/utils/helper';
import { getScopeType } from '../utils';

const { t } = useTranslation();

const props = defineProps<{
  initialData?: TextDto[];
}>();

const modelValue = defineModel<TextOverviewFilterObject>({
  required: true,
});

const setupScopeOptions = computed<SelectItemTypeObject<TextScopeFilterObject>[]>(() => {
  const entries = (props.initialData ?? [])
    .flatMap((text) => text.setupScope ?? [])
    .map((scope): TextScopeFilterObject | undefined => {
      const option = scope.rtSystemSubCategory;
      return option
        ? { option, property: 'rtSystemSubCategory', scope: getScopeType(scope) }
        : undefined;
    })
    .filter(truthy);

  return uniqBy(entries, (entry) => `${entry.scope}.${entry.option}`).map((entry) => ({
    title: t(`${getTranslationPrefix(entry.scope, entry.property)}.${entry.option}` as I18nKey),
    value: entry,
  }));
});

function clearFilters() {
  modelValue.value = {};
}
</script>

<template>
  <v-row class="align-center mt-2 pl-2 pr-2">
    <v-col cols="12" sm="6" md="3" lg="3" class="pa-2">
      <SearchField v-model:modelValue="modelValue.search" :placeholder="$t('Search')" />
    </v-col>

    <v-col cols="12" sm="6" md="3" lg="3" class="pa-2">
      <!-- Setup Category -->
      <Autocomplete
        v-model="modelValue.setupScope"
        :label="$t('SetupCategory_other')"
        :items="setupScopeOptions"
        :placeholder="$t('All')"
        :maxNumDisplayItems="2"
        multiple
        clearable
        chips
        emptyAsUndefined
      />
    </v-col>

    <v-col md="3" lg="1" class="d-flex align-center justify-start pa-2">
      <PtwResetFilterButton @click="clearFilters" />
    </v-col>
  </v-row>
</template>
